import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AlertCircle,
  AlertTriangle,
  BookMarked,
  BookOpen,
  Bookmark,
  Compass,
  Layers,
  LayoutGrid,
  List,
  RefreshCw,
  Search,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import PageHeader from "../../Components/PageHeader";
import GroupedBrowseLayout from "../../Components/sponsor-tips/GroupedBrowseLayout";
import LibrarySubtabIntro from "../../Components/sponsor-tips/LibrarySubtabIntro";
import TipCard from "../../Components/sponsor-tips/TipCard";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchSponsorTipsBookmarks,
  fetchSponsorTipsForAudience,
  fetchSponsorTipsRecommended,
} from "../../Store/Reducers/SponsorTipsSlice";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import {
  ACCOUNT_TEAM_SUB_TABS,
  AUDIENCE_API_MAP,
  AUDIENCE_TABS,
  categoryDescription,
  categoryLabel,
  INITIAL_RECOMMENDED,
  journeyPhaseLabel,
  LEVEL_ORDER,
  LEVEL_PLAYBOOK_DESCRIPTIONS,
  levelLabel,
  LIBRARY_BROWSE_SCROLL_ATTR,
  LIBRARY_DISPLAY_NAME,
  LIBRARY_SUBTITLE,
  PROGRAM_LEADER_SUB_TABS,
  ROLE_LENS_LABELS,
  ROLE_LENS_ORDER,
  saveLibraryViewState,
  SENIORITY_LABELS,
  SENIORITY_ORDER,
  SPONSOR_JOURNEY_BROWSE_KEY,
  SPONSOR_JOURNEY_CATEGORIES,
  SPONSOR_SUB_TABS,
  TELEMETRY_EVENTS,
  trackSponsorTipsEvent,
  type AudienceTab,
  type LibraryReturnState,
  type LibrarySubTab,
  type SponsorTip,
} from "../../Utils/sponsorTipsConstants";
import {
  browseTipsGridClass,
  collapseJourneyCategoryGroups,
  groupBy,
  groupTipsByLevels,
  groupTipsByRoleLens,
  groupTipsBySeniority,
  sortAccountTeamCategoryGroups,
  sortCategoryGroups,
  sortProgramLeaderCategoryGroups,
} from "../../Utils/sponsorTipsGrouping";

const SUBTAB_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  recommended: Sparkles,
  saved: Bookmark,
  by_category: LayoutGrid,
  what_to_avoid: AlertTriangle,
  playbooks: BookMarked,
  by_level: Layers,
  by_seniority: Users,
  by_role: Users,
  all: List,
};

const parseAudience = (value: string | null): AudienceTab => {
  if (value === "account_teams" || value === "program_leaders") return value;
  return "sponsors";
};

const getSubTabsForAudience = (audience: AudienceTab) => {
  if (audience === "account_teams") return ACCOUNT_TEAM_SUB_TABS;
  if (audience === "program_leaders") return PROGRAM_LEADER_SUB_TABS;
  return SPONSOR_SUB_TABS;
};

const getDefaultSubtabForAudience = (audience: AudienceTab): LibrarySubTab =>
  getSubTabsForAudience(audience)[0]?.id ?? "recommended";

const resolvePendingScrollTop = (
  location: ReturnType<typeof useLocation>
): number | undefined => {
  const restore = (
    location.state as { restoreLibrary?: LibraryReturnState } | null
  )?.restoreLibrary;
  if (restore?.scrollTop == null) return undefined;

  const params = new URLSearchParams(location.search);
  const tab = parseAudience(params.get("tab"));
  const defaultSubtab = getDefaultSubtabForAudience(tab);
  const subtabRaw = params.get("subtab") || defaultSubtab;
  const activeSubtabs = getSubTabsForAudience(tab);
  const currentSubtab = activeSubtabs.some(t => t.id === subtabRaw)
    ? subtabRaw
    : defaultSubtab;

  if ((restore.tab || "sponsors") !== tab || restore.subtab !== currentSubtab) {
    return undefined;
  }

  return restore.scrollTop;
};

const renderJourneyPhaseTipsGrid = (tips: SponsorTip[]) => (
  <div className="space-y-6">
    {SPONSOR_JOURNEY_CATEGORIES.map(phase => {
      const phaseTips = tips.filter(t => t.category === phase);
      if (!phaseTips.length) return null;
      return (
        <div key={phase}>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-teal-800">
            {journeyPhaseLabel(phase)}
          </h4>
          <div className={browseTipsGridClass(phaseTips.length)}>
            {phaseTips.map(t => (
              <TipCard key={t.id} tip={t} suppressCategoryBadge />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const renderCategoryTipsGrid = (groupKey: string, tips: SponsorTip[]) => {
  if (groupKey === SPONSOR_JOURNEY_BROWSE_KEY) {
    return renderJourneyPhaseTipsGrid(tips);
  }
  return (
    <div className={browseTipsGridClass(tips.length)}>
      {tips.map(t => (
        <TipCard key={t.id} tip={t} suppressCategoryBadge />
      ))}
    </div>
  );
};

const GUIDANCE_HUB_BANNER_DISMISSED_KEY = "eb_guidance_hub_banner_dismissed";

const SponsorTipsLibraryContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = user?.user?.role || loggedInUser?.role;
  const { hubEnabled, hubEnabledRoles } = useAppSelector(
    state => state.guidance
  );
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    userRole
  );
  const [guidanceHubBannerDismissed, setGuidanceHubBannerDismissed] = useState(
    () => {
      try {
        return localStorage.getItem(GUIDANCE_HUB_BANNER_DISMISSED_KEY) === "1";
      } catch {
        return false;
      }
    }
  );
  const browseScrollRef = useRef<HTMLDivElement>(null);
  const scrollRestoredRef = useRef(false);
  const [pendingScrollTop, setPendingScrollTop] = useState<number | undefined>(
    () => resolvePendingScrollTop(location)
  );
  const bookmarkedSlugs = useAppSelector(
    state => state.sponsorTips.bookmarks.slugs
  );
  const [showAllRecommended, setShowAllRecommended] = useState(false);

  const audience = parseAudience(searchParams.get("tab"));
  const defaultSubtab = getDefaultSubtabForAudience(audience);
  const subtabRaw = searchParams.get("subtab") || defaultSubtab;
  const activeSubtabs = getSubTabsForAudience(audience);
  const subtab = activeSubtabs.some(t => t.id === subtabRaw)
    ? (subtabRaw as LibrarySubTab)
    : defaultSubtab;

  const apiAudience = AUDIENCE_API_MAP[audience];
  const audienceCache = useAppSelector(
    state => state.sponsorTips.listsByAudience[apiAudience]
  );
  const recommendedCache = useAppSelector(
    state => state.sponsorTips.recommended
  );

  const allTips = audienceCache?.tips ?? [];
  const loadingAll = audienceCache?.loading ?? false;
  const allError = audienceCache?.error;
  const recommended = recommendedCache.tips;
  const loadingRec = recommendedCache.loading;
  const recError = recommendedCache.error;

  const loadAllTips = useCallback(
    (force = false) => {
      dispatch(fetchSponsorTipsForAudience({ audience: apiAudience, force }));
    },
    [dispatch, apiAudience]
  );

  const loadRecommended = useCallback(
    (force = false) => {
      if (audience === "sponsors") {
        dispatch(fetchSponsorTipsRecommended(force));
      }
    },
    [dispatch, audience]
  );

  const loadData = useCallback(
    (force = false) => {
      if (audience === "sponsors") {
        loadAllTips(force);
        loadRecommended(force);
      } else {
        loadAllTips(force);
      }
    },
    [audience, loadAllTips, loadRecommended]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    dispatch(fetchSponsorTipsBookmarks());
  }, [dispatch]);

  useEffect(() => {
    trackSponsorTipsEvent(TELEMETRY_EVENTS.LIBRARY_VIEWED, {
      tab: audience,
      subtab,
    });
    if (subtab === "what_to_avoid") {
      trackSponsorTipsEvent("what_to_avoid_viewed");
    }
  }, [audience, subtab]);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    saveLibraryViewState({
      tab: audience,
      subtab,
      hash: hash || undefined,
      scrollTop:
        browseScrollRef.current && browseScrollRef.current.scrollTop > 0
          ? browseScrollRef.current.scrollTop
          : undefined,
    });
  }, [audience, subtab]);

  useEffect(() => {
    const root = browseScrollRef.current;
    if (!root) return undefined;

    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      if (pendingScrollTop != null) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        saveLibraryViewState({
          tab: audience,
          subtab,
          hash: window.location.hash.replace(/^#/, "") || undefined,
          scrollTop: root.scrollTop > 0 ? root.scrollTop : undefined,
        });
      }, 150);
    };

    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, [audience, subtab, pendingScrollTop]);

  const resetBrowseView = () => {
    setShowAllRecommended(false);
    setPendingScrollTop(undefined);
    scrollRestoredRef.current = false;
    window.requestAnimationFrame(() => {
      browseScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  };

  const groupedBrowseScrollRestore = pendingScrollTop ?? null;

  const setAudience = (id: string) => {
    const next = new URLSearchParams(searchParams);
    const tab = parseAudience(id);
    const nextSubtab = getDefaultSubtabForAudience(tab);

    if (tab === "sponsors") {
      next.delete("tab");
    } else {
      next.set("tab", tab);
    }
    next.set("subtab", nextSubtab);
    next.delete("category");
    setSearchParams(next, { replace: true });
    resetBrowseView();
  };

  const setSubTab = (id: string) => {
    trackSponsorTipsEvent(TELEMETRY_EVENTS.FILTER_APPLIED, { subtab: id });
    const next = new URLSearchParams(searchParams);
    next.set("subtab", id);
    next.delete("category");
    setSearchParams(next, { replace: true });
    resetBrowseView();
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filterBySearch = useCallback(
    (tips: SponsorTip[], query: string): SponsorTip[] => {
      if (!query) return tips;
      const tokens = Array.from(
        new Set(query.toLowerCase().split(/\s+/).filter(Boolean))
      );
      if (!tokens.length) return tips;

      return tips.filter(t => {
        const haystack = [t.title, t.email_short, t.body]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return tokens.every(token => haystack.includes(token));
      });
    },
    []
  );

  const sortedAll = useMemo(
    () =>
      filterBySearch([...allTips], debouncedSearch).sort((a, b) =>
        a.title.localeCompare(b.title)
      ),
    [allTips, debouncedSearch, filterBySearch]
  );

  const groupedByCategory = useMemo(() => {
    const grouped = groupBy(sortedAll, t => t.category);
    if (audience === "account_teams") {
      return sortAccountTeamCategoryGroups(grouped);
    }
    if (audience === "program_leaders") {
      return sortProgramLeaderCategoryGroups(grouped);
    }
    return collapseJourneyCategoryGroups(sortCategoryGroups(grouped));
  }, [sortedAll, audience]);

  const groupedByLevel = useMemo(
    () => groupTipsByLevels(sortedAll, LEVEL_ORDER),
    [sortedAll]
  );

  const groupedBySeniority = useMemo(
    () => groupTipsBySeniority(sortedAll, SENIORITY_ORDER),
    [sortedAll]
  );

  const failureModeTips = useMemo(
    () => sortedAll.filter(t => t.tip_type === "failure_mode"),
    [sortedAll]
  );

  const groupedFailureByCategory = useMemo(() => {
    const grouped = groupBy(failureModeTips, t => t.category);
    if (audience === "account_teams") {
      return sortAccountTeamCategoryGroups(grouped);
    }
    if (audience === "program_leaders") {
      return sortProgramLeaderCategoryGroups(grouped);
    }
    return collapseJourneyCategoryGroups(sortCategoryGroups(grouped));
  }, [failureModeTips, audience]);

  const groupedByRole = useMemo(
    () => groupTipsByRoleLens(sortedAll, ROLE_LENS_ORDER),
    [sortedAll]
  );

  const playbookTips = useMemo(
    () => sortedAll.filter(t => t.category === "level_progression"),
    [sortedAll]
  );

  const groupedPlaybooksByLevel = useMemo(
    () => groupTipsByLevels(playbookTips, LEVEL_ORDER),
    [playbookTips]
  );

  const savedTips = useMemo(
    () => sortedAll.filter(t => bookmarkedSlugs.includes(t.url_slug)),
    [sortedAll, bookmarkedSlugs]
  );

  const filteredRecommended = useMemo(
    () => filterBySearch(recommended, debouncedSearch),
    [recommended, debouncedSearch, filterBySearch]
  );

  const visibleRecommended = showAllRecommended
    ? filteredRecommended
    : filteredRecommended.slice(0, INITIAL_RECOMMENDED);

  const subTabItems = useMemo(
    () =>
      activeSubtabs.map(t => {
        const Icon = SUBTAB_ICONS[t.id] || List;
        return {
          id: t.id,
          label: t.label,
          icon: <Icon className="h-4 w-4" />,
        };
      }),
    [activeSubtabs]
  );

  const isLoading =
    loadingAll ||
    (audience === "sponsors" && subtab === "recommended" && loadingRec);

  const subtabHasResults = useCallback(
    (tab: string): boolean => {
      switch (tab) {
        case "recommended":
          return filteredRecommended.length > 0;
        case "saved":
          return savedTips.length > 0;
        case "by_category":
          return groupedByCategory.length > 0;
        case "what_to_avoid":
          return groupedFailureByCategory.length > 0;
        case "playbooks":
          return groupedPlaybooksByLevel.length > 0;
        case "by_level":
          return groupedByLevel.length > 0;
        case "by_seniority":
          return groupedBySeniority.length > 0;
        case "by_role":
          return groupedByRole.length > 0;
        case "all":
          return sortedAll.length > 0;
        default:
          return true;
      }
    },
    [
      filteredRecommended,
      savedTips,
      groupedByCategory,
      groupedFailureByCategory,
      groupedPlaybooksByLevel,
      groupedByLevel,
      groupedBySeniority,
      groupedByRole,
      sortedAll,
    ]
  );

  useEffect(() => {
    if (!debouncedSearch || isLoading) return;
    if (subtabHasResults(subtab)) return;

    const match = (
      activeSubtabs as readonly { id: string; label: string }[]
    ).find(t => t.id !== subtab && subtabHasResults(t.id));
    if (match) {
      const next = new URLSearchParams(searchParams);
      next.set("subtab", match.id);
      next.delete("category");
      setSearchParams(next, { replace: true });
    }
  }, [
    debouncedSearch,
    isLoading,
    subtab,
    activeSubtabs,
    subtabHasResults,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (pendingScrollTop == null || scrollRestoredRef.current) return;
    if (isLoading && allTips.length === 0) return;

    const root = browseScrollRef.current;
    if (!root) return;

    scrollRestoredRef.current = true;
    const targetTop = pendingScrollTop;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.scrollTo({ top: targetTop, behavior: "auto" });
        root.dispatchEvent(new Event("scroll"));
        setPendingScrollTop(undefined);
        navigate(
          {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
          },
          { replace: true, state: null }
        );
      });
    });
  }, [
    pendingScrollTop,
    isLoading,
    allTips.length,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const renderTipGrid = (tips: SponsorTip[]) => (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {tips.map(t => (
        <TipCard key={t.id} tip={t} />
      ))}
    </div>
  );

  const renderEmpty = (message: string, action?: React.ReactNode) => (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      <BookOpen className="mb-4 h-12 w-12 text-gray-300" />
      <p className="m-0 max-w-md text-sm text-gray-600">{message}</p>
      {action}
    </div>
  );

  const seniorityLabel = (k: string) => SENIORITY_LABELS[k] || k;

  const renderBrowseContent = () => {
    if (allError && allTips.length === 0) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-red-800">{allError}</p>
            <button
              type="button"
              onClick={() => loadData(true)}
              className="mt-3 inline-flex items-center text-sm font-medium text-red-700 hover:underline"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (isLoading && allTips.length === 0) {
      return (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-600 border-t-transparent" />
        </div>
      );
    }

    if (subtab === "saved") {
      return (
        <div className="pt-4">
          {savedTips.length
            ? renderTipGrid(savedTips)
            : renderEmpty(
                "Nothing saved yet. Bookmark guidance from any card or detail page to keep it in your saved library.",
                <button
                  type="button"
                  onClick={() => setSubTab("recommended")}
                  className="mt-4 text-sm font-medium text-purple-600 hover:underline"
                >
                  Browse recommended guidance →
                </button>
              )}
        </div>
      );
    }

    if (subtab === "by_role") {
      return groupedByRole.length ? (
        <GroupedBrowseLayout
          key={`${audience}:${subtab}:role`}
          scrollRootRef={browseScrollRef}
          initialScrollTop={groupedBrowseScrollRestore}
          groups={groupedByRole}
          anchorPrefix="role"
          navAriaLabel="Role lens"
          getLabel={k => ROLE_LENS_LABELS[k] || k}
          telemetryEvent="role_lens_nav_clicked"
          introTitle="By role"
          intro="Guidance tagged for AE, CSM, or both — many items apply to the full account team."
        />
      ) : (
        renderEmpty("No guidance in this view.")
      );
    }

    if (subtab === "recommended") {
      if (recError && recommended.length === 0 && !loadingRec) {
        return (
          <div className="pt-4">
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm leading-relaxed text-amber-900">
              {recError}. Showing universal guidance below — or switch to{" "}
              <strong className="font-semibold">All guidance</strong>.
            </div>
            {sortedAll.length > 0
              ? renderTipGrid(sortedAll.slice(0, INITIAL_RECOMMENDED))
              : renderEmpty("No guidance available yet.")}
          </div>
        );
      }

      if (filteredRecommended.length === 0 && !loadingRec) {
        return (
          <div className="pt-4">
            {renderEmpty(
              "No exactly-matched guidance for your portfolio yet. Here are universally applicable items you can start with.",
              sortedAll.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setSubTab("all")}
                  className="mt-4 text-sm font-medium text-purple-600 hover:underline"
                >
                  Browse all {sortedAll.length} items →
                </button>
              ) : null
            )}
          </div>
        );
      }

      return (
        <div className="pt-4">
          <LibrarySubtabIntro title="Recommended for you">
            Based on your portfolio mix and seniority when available.
          </LibrarySubtabIntro>
          {loadingRec ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent" />
            </div>
          ) : (
            <>
              {renderTipGrid(visibleRecommended)}
              {filteredRecommended.length > INITIAL_RECOMMENDED &&
              !showAllRecommended ? (
                <button
                  type="button"
                  onClick={() => setShowAllRecommended(true)}
                  className="mt-5 text-sm font-medium text-purple-600 hover:underline"
                >
                  See all {filteredRecommended.length} recommended items →
                </button>
              ) : null}
              <p className="mt-3 text-xs leading-relaxed text-gray-500">
                Showing {visibleRecommended.length} of{" "}
                {filteredRecommended.length} recommended
              </p>
            </>
          )}
        </div>
      );
    }

    if (subtab === "by_category") {
      return groupedByCategory.length ? (
        <GroupedBrowseLayout
          key={`${audience}:${subtab}:category`}
          scrollRootRef={browseScrollRef}
          initialScrollTop={groupedBrowseScrollRestore}
          groups={groupedByCategory}
          anchorPrefix="category"
          navAriaLabel="Categories"
          getLabel={categoryLabel}
          getDescription={categoryDescription}
          telemetryEvent="category_nav_clicked"
          renderTipsGrid={renderCategoryTipsGrid}
        />
      ) : (
        renderEmpty("No guidance in this view.")
      );
    }

    if (subtab === "what_to_avoid") {
      return groupedFailureByCategory.length ? (
        <GroupedBrowseLayout
          key={`${audience}:${subtab}:avoid`}
          scrollRootRef={browseScrollRef}
          initialScrollTop={groupedBrowseScrollRestore}
          groups={groupedFailureByCategory}
          anchorPrefix="avoid"
          navAriaLabel="Pitfall categories"
          getLabel={categoryLabel}
          getDescription={categoryDescription}
          telemetryEvent="what_to_avoid_nav_clicked"
          introTitle="What to avoid"
          intro="Pitfalls grouped by topic; counts vary by area. Each item links to a paired best practice on its detail page."
          renderTipsGrid={renderCategoryTipsGrid}
        />
      ) : (
        renderEmpty("No failure-mode guidance in this view.")
      );
    }

    if (subtab === "playbooks") {
      return (
        <div className="pt-4">
          <LibrarySubtabIntro title="More playbooks coming soon">
            Risks, opportunities, sentiment, and detection playbooks arrive
            after a focused design session. Below: Account Level progression
            playbooks (also under By category → Account Level progression).
          </LibrarySubtabIntro>
          {groupedPlaybooksByLevel.length ? (
            <GroupedBrowseLayout
              key={`${audience}:${subtab}:playbook-level`}
              scrollRootRef={browseScrollRef}
              initialScrollTop={groupedBrowseScrollRestore}
              groups={groupedPlaybooksByLevel}
              anchorPrefix="playbook-level"
              navAriaLabel="XFACTOR Account Levels"
              getLabel={levelLabel}
              getDescription={k => LEVEL_PLAYBOOK_DESCRIPTIONS[k] || ""}
              telemetryEvent="playbook_nav_clicked"
            />
          ) : (
            renderEmpty("No Account Level progression playbooks yet.")
          )}
        </div>
      );
    }

    if (subtab === "by_level") {
      return groupedByLevel.length ? (
        <GroupedBrowseLayout
          key={`${audience}:${subtab}:level`}
          scrollRootRef={browseScrollRef}
          initialScrollTop={groupedBrowseScrollRestore}
          groups={groupedByLevel}
          anchorPrefix="level"
          navAriaLabel="XFACTOR Account Levels"
          getLabel={levelLabel}
          telemetryEvent="level_nav_clicked"
        />
      ) : (
        renderEmpty("No guidance in this view.")
      );
    }

    if (subtab === "by_seniority") {
      return groupedBySeniority.length ? (
        <GroupedBrowseLayout
          key={`${audience}:${subtab}:seniority`}
          scrollRootRef={browseScrollRef}
          initialScrollTop={groupedBrowseScrollRestore}
          groups={groupedBySeniority}
          anchorPrefix="seniority"
          navAriaLabel="Seniority"
          getLabel={seniorityLabel}
          telemetryEvent="seniority_nav_clicked"
        />
      ) : (
        renderEmpty("No guidance in this view.")
      );
    }

    return (
      <div className="pt-4">
        {sortedAll.length
          ? renderTipGrid(sortedAll)
          : renderEmpty("No guidance available.")}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-gray-50 mt-3">
      <PageHeader
        title={LIBRARY_DISPLAY_NAME}
        icon={BookOpen}
        subTitle={LIBRARY_SUBTITLE}
        actions={[
          <button
            key="refresh"
            type="button"
            onClick={() => loadData(true)}
            disabled={isLoading}
            className={`inline-flex items-center text-sm text-gray-600 hover:text-[#974ae4] disabled:opacity-50 ${
              hubActive ? "mr-12" : ""
            }`}
            title="Refresh library"
          >
            <RefreshCw
              className={`mr-1 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>,
        ]}
      />

      {hubActive && !guidanceHubBannerDismissed && (
        <div className="shrink-0 flex w-full items-center justify-between gap-3 bg-purple-50 border-b border-purple-100 px-4 py-2 md:px-6">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-purple-800">
            <Compass className="h-4 w-4 shrink-0" />
            <span>Looking for cadence playbooks?</span>
            <button
              type="button"
              onClick={() =>
                navigate(
                  orgSlug
                    ? `/${orgSlug}/guidance/playbooks`
                    : "/guidance/playbooks"
                )
              }
              className="font-semibold text-purple-700 hover:text-purple-800"
            >
              Open Playbooks Hub →
            </button>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              setGuidanceHubBannerDismissed(true);
              try {
                localStorage.setItem(GUIDANCE_HUB_BANNER_DISMISSED_KEY, "1");
              } catch {
                // ignore — worst case the banner reappears next visit
              }
            }}
            className="shrink-0 text-purple-400 hover:text-purple-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 md:px-6">
        <div className="flex flex-col gap-3">
          <div className="w-full min-w-0 overflow-x-auto scrollbar-hide">
            <TabsComponent
              tabs={AUDIENCE_TABS}
              activeTab={audience}
              onTabChange={setAudience}
              variant="tab-segmented"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <TabsComponent
                tabs={subTabItems}
                activeTab={subtab}
                onTabChange={setSubTab}
                variant="tab-underline"
                className="mb-0"
                scrollable={audience === "sponsors"}
              />
            </div>
            <div className="relative shrink-0 w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search guidance…"
                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden bg-gray-50 p-4">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-[bg-white] bg-white">
          <div
            ref={browseScrollRef}
            {...{ [LIBRARY_BROWSE_SCROLL_ATTR]: "" }}
            className="min-h-0 flex-1 overflow-y-auto"
          >
            <div className="px-4 pb-4">{renderBrowseContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SponsorTipsLibrary: React.FC = () => <SponsorTipsLibraryContent />;

export default SponsorTipsLibrary;
